import {
  IpmErrorBoundary,
  reportIpmAction,
  reportIpmDismiss,
  reportIpmVisible,
  type IpmMetricTags,
  useIpmRenderTiming,
} from '@github-ui/ipm-telemetry'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {Profiler, useEffect, useMemo, useRef, useState} from 'react'
import styles from './OrgOverviewSidebarNudge.module.css'

export interface OrgOverviewSidebarNudgeAnalytics {
  category: string
  group: string
  location: string
  ctaAction: string
  organizationId?: number | string
  plan?: string
}

export interface OrgOverviewSidebarNudgeProps {
  title: string
  dateLabel: string
  imagePathLight: string
  imagePathDark: string
  imageAltLight?: string
  imageAltDark?: string
  description?: string
  topics?: string[]
  ctaTitle: string
  ctaHref: string
  dismissPath: string
  analytics: OrgOverviewSidebarNudgeAnalytics
  testSelector: string
}

export function OrgOverviewSidebarNudge({
  title,
  dateLabel,
  imagePathLight,
  imagePathDark,
  imageAltLight = '',
  imageAltDark = '',
  description,
  topics,
  ctaTitle,
  ctaHref,
  dismissPath,
  analytics,
  testSelector,
}: OrgOverviewSidebarNudgeProps) {
  const [hidden, setHidden] = useState(false)
  const dismissAnnouncementRef = useRef<HTMLSpanElement>(null)
  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'OrgOverviewSidebarNudge',
      display_location: analytics.location,
      category: analytics.category,
      nudge_id: testSelector,
      variant: 'default',
    }),
    [analytics.category, analytics.location, testSelector],
  )
  const renderTiming = useIpmRenderTiming(telemetryTags)

  useEffect(() => {
    reportIpmVisible(telemetryTags)
  }, [telemetryTags])

  const handleDismiss = async () => {
    reportIpmDismiss(telemetryTags)
    try {
      await verifiedFetch(dismissPath, {method: 'POST'})
    } catch {
      reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      // Silently handle network errors to maintain good UX
    } finally {
      setHidden(true)
    }
  }

  // After the card unmounts, move focus to the polite live region that
  // announces the dismissal. Without this, keyboard / screen-reader users get
  // dumped onto <body> when the focused dismiss button is removed (WCAG 2.4.3).
  useEffect(() => {
    if (hidden) {
      dismissAnnouncementRef.current?.focus()
    }
  }, [hidden])

  if (hidden) {
    return (
      <span
        ref={dismissAnnouncementRef}
        tabIndex={-1}
        role="status"
        aria-live="polite"
        className={styles.dismissAnnouncement}
      >
        Nudge dismissed.
      </span>
    )
  }

  const analyticsLabel = `group:${analytics.group};location:${analytics.location}`

  const nudge = (
    <>
      <span data-test-selector={testSelector} />
      <div
        className={styles.container}
        data-hpc
        data-testid="org-overview-sidebar-nudge"
        data-analytics-visible={JSON.stringify({
          category: analytics.category,
          action: 'visible',
          label: analyticsLabel,
          ...(analytics.organizationId !== undefined && {organizationId: analytics.organizationId}),
          ...(analytics.plan !== undefined && {plan: analytics.plan}),
        })}
      >
        <div className={styles.dismiss}>
          <IconButton
            icon={XIcon}
            variant="invisible"
            aria-label="Dismiss"
            onClick={handleDismiss}
            data-analytics-event={JSON.stringify({
              category: analytics.category,
              action: 'dismiss',
              label: analyticsLabel,
            })}
          />
        </div>

        <img src={imagePathLight} alt={imageAltLight} className={`${styles.image} ${styles.imageLight}`} />
        <img src={imagePathDark} alt={imageAltDark} className={`${styles.image} ${styles.imageDark}`} />

        <div className={styles.date}>{dateLabel}</div>

        <div className={styles.main}>
          <h3 className={styles.title}>{title}</h3>

          {description ? <p className={styles.description}>{description}</p> : null}

          {topics && topics.length > 0 ? (
            // Safari + VoiceOver drop list semantics from a <ul> whose
            // list-style is `none`. Restate the role explicitly to keep
            // assistive tech announcing the topics as a list.
            // eslint-disable-next-line jsx-a11y/no-redundant-roles
            <ul className={styles.topics} role="list">
              {topics.map(topic => (
                <li key={topic} className={styles.topic}>
                  {topic}
                </li>
              ))}
            </ul>
          ) : null}

          {/* `btn` and `btn-sm` provide layout + size; colors come from the CSS
              module so we don't fight `btn-primary` specificity with !important. */}
          <a
            href={ctaHref}
            onClick={() => reportIpmAction('cta_click', telemetryTags)}
            className={`${styles.cta} btn btn-sm text-small px-2`}
            data-test-selector={`${testSelector}-cta`}
            data-analytics-event={JSON.stringify({
              category: analytics.category,
              action: analytics.ctaAction,
              label: analyticsLabel,
            })}
          >
            {ctaTitle}
          </a>
        </div>
      </div>
    </>
  )

  const content = !renderTiming.isEnabled ? (
    nudge
  ) : (
    <Profiler id="OrgOverviewSidebarNudge" onRender={renderTiming.onRender}>
      {nudge}
    </Profiler>
  )

  return (
    <IpmErrorBoundary tags={telemetryTags} boundaryName="OrgOverviewSidebarNudge">
      {content}
    </IpmErrorBoundary>
  )
}
